/**
 * RulerTool Component
 * Interactive distance measurement tool for Leaflet maps
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 863-1024)
 * Google Maps-style ruler with segment and total distance labels
 */

import React, { useState, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { CircleMarker, Polyline, Marker } from 'react-leaflet';
import L from 'leaflet';
import { formatDistance as utilFormatDistance } from '../../../utils/formatters';

// Helper to calculate distance between two [lat, lng] points
function calculateDistanceTuple(p1: [number, number], p2: [number, number]): number {
  const [lat1, lng1] = p1;
  const [lat2, lng2] = p2;

  // Haversine formula
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // Distance in meters
}

export interface RulerToolProps {
  isActive: boolean;
  points: [number, number][];
  onAddPoint: (point: [number, number]) => void;
  onClear: () => void;
  calculateDistance?: (p1: [number, number], p2: [number, number]) => number;
  formatDistance?: (meters: number) => string;
}

/**
 * Calculate total distance for a series of points
 */
function calculateTotalDistance(
  points: [number, number][],
  distanceFn: (p1: [number, number], p2: [number, number]) => number
): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += distanceFn(points[i - 1], points[i]);
  }
  return total;
}

/**
 * Interactive ruler tool component
 * Displays distance measurements along each segment and total distance
 */
export function RulerTool({
  isActive,
  points,
  onAddPoint,
  onClear,
  calculateDistance = calculateDistanceTuple,
  formatDistance = utilFormatDistance,
}: RulerToolProps) {
  const map = useMap();
  const [cursorPos, setCursorPos] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!isActive) {
      setCursorPos(null);
      return;
    }

    const handleClick = (e: any) => {
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();
      onAddPoint([e.latlng.lat, e.latlng.lng]);
    };

    const handleDblClick = (e: any) => {
      e.originalEvent.preventDefault();
      e.originalEvent.stopPropagation();
      // Double-click finishes measurement
      onClear();
    };

    const handleMouseMove = (e: any) => {
      setCursorPos([e.latlng.lat, e.latlng.lng]);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClear();
      }
    };

    map.on('click', handleClick);
    map.on('dblclick', handleDblClick);
    map.on('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      map.off('click', handleClick);
      map.off('dblclick', handleDblClick);
      map.off('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, map, onAddPoint, onClear]);

  if (!isActive) return null;

  // Calculate distances for display
  const totalDistance = points.length > 1 ? calculateTotalDistance(points, calculateDistance) : 0;
  const cursorDistance =
    points.length > 0 && cursorPos ? calculateDistance(points[points.length - 1], cursorPos) : 0;

  return (
    <>
      {/* Point markers - Bold empty circles like Google Maps */}
      {points.map((point, index) => (
        <CircleMarker
          key={`ruler-point-${index}`}
          center={point}
          // @ts-ignore - react-leaflet v5 supports radius but v2 types don't
          radius={8}
          pathOptions={{
            color: '#EBFD03',
            fillColor: '#EBFD03',
            fillOpacity: 0,
            weight: 4,
          }}
        />
      ))}

      {/* Polyline connecting points - Bold Yellow Dotted */}
      {points.length > 1 && (
        <Polyline
          positions={points}
          pathOptions={{
            color: '#EBFD03',
            weight: 4,
            dashArray: '10, 10',
          }}
        />
      )}

      {/* Preview line from last point to cursor - Bold Yellow Dotted */}
      {points.length > 0 && cursorPos && (
        <Polyline
          positions={[points[points.length - 1], cursorPos]}
          pathOptions={{
            color: '#EBFD03',
            weight: 4,
            dashArray: '10, 10',
            opacity: 0.7,
          }}
        />
      )}

      {/* Segment distance labels - Google Maps style with text shadow */}
      {points.length > 1 &&
        points.map((point, index) => {
          if (index === 0) return null; // Skip first point (no segment before it)

          const prevPoint = points[index - 1];
          const midLat = (prevPoint[0] + point[0]) / 2;
          const midLng = (prevPoint[1] + point[1]) / 2;
          const segmentDist = calculateDistance(prevPoint, point);

          return (
            <Marker
              key={`ruler-label-${index}`}
              position={[midLat, midLng]}
              // @ts-ignore - react-leaflet v5 supports icon but v2 types don't
              icon={L.divIcon({
                className: 'ruler-label',
                html: `<div style="color: #000000; font-size: 14px; font-weight: bold; white-space: nowrap; text-shadow: -1px -1px 0 #EBFD03, 1px -1px 0 #EBFD03, -1px 1px 0 #EBFD03, 1px 1px 0 #EBFD03, -2px 0 0 #EBFD03, 2px 0 0 #EBFD03, 0 -2px 0 #EBFD03, 0 2px 0 #EBFD03;">${formatDistance(segmentDist)}</div>`,
                iconSize: [0, 0],
              })}
            />
          );
        })}

      {/* Live cursor distance label - Google Maps style */}
      {points.length > 0 && cursorPos && (
        <>
          {/* Segment distance to cursor */}
          <Marker
            position={[
              (points[points.length - 1][0] + cursorPos[0]) / 2,
              (points[points.length - 1][1] + cursorPos[1]) / 2,
            ]}
            // @ts-ignore - react-leaflet v5 supports icon but v2 types don't
            icon={L.divIcon({
              className: 'ruler-cursor-label',
              html: `<div style="color: #000000; font-size: 14px; font-weight: bold; white-space: nowrap; text-shadow: -1px -1px 0 #EBFD03, 1px -1px 0 #EBFD03, -1px 1px 0 #EBFD03, 1px 1px 0 #EBFD03, -2px 0 0 #EBFD03, 2px 0 0 #EBFD03, 0 -2px 0 #EBFD03, 0 2px 0 #EBFD03; opacity: 0.8;">+${formatDistance(cursorDistance)}</div>`,
              iconSize: [0, 0],
            })}
          />

          {/* Total distance at cursor */}
          <Marker
            position={cursorPos}
            // @ts-ignore - react-leaflet v5 supports icon but v2 types don't
            icon={L.divIcon({
              className: 'ruler-total-label',
              html: `<div style="color: #000000; font-size: 15px; font-weight: bold; white-space: nowrap; text-shadow: -1.5px -1.5px 0 #EBFD03, 1.5px -1.5px 0 #EBFD03, -1.5px 1.5px 0 #EBFD03, 1.5px 1.5px 0 #EBFD03, -2.5px 0 0 #EBFD03, 2.5px 0 0 #EBFD03, 0 -2.5px 0 #EBFD03, 0 2.5px 0 #EBFD03; margin-top: -30px;">Total: ${formatDistance(totalDistance + cursorDistance)}</div>`,
              iconSize: [0, 0],
            })}
          />
        </>
      )}
    </>
  );
}
